import {User} from '@hconnect/apiclient'
import React, {useState} from 'react'

import {
  RoleConfiguration,
  useRolesConfigurationForUserId
} from '../common/hooks/useRolesConfiguration'
import {useLoggedInUserPermissions} from '../hooks/useLoggedInUser'
import {RoleViewLayout} from '../layouts/Roles/RoleViewLayout'
import {getDataScopeWeight} from '../modules/DataScopeConfiguration'
import {Permission} from '../modules/LoggedInUser'
import {GroupedRoleAssignment, RoleAssignment} from '../modules/ManageUsers.selectors'

import {RoleEditContainer} from './RoleEditContainer'

interface RoleViewContainerProps {
  groupedRoleAssignment: GroupedRoleAssignment
  updateRoleAssignments: (groupedRoleAssignment: GroupedRoleAssignment) => Promise<void>
  getRoles: () => void
  roleAssignments: RoleAssignment[]
  loggedInUserPermissions?: Permission[] | null
  loggedInUserProfile?: User | null
}

// Client-site workaround until we get orderWeight from the roleConfiguration (backend)
const sortDataScopes = (dataScopeA: string, dataScopeB: string) =>
  getDataScopeWeight(dataScopeA) - getDataScopeWeight(dataScopeB)

export const RoleViewContainer: React.FC<RoleViewContainerProps> = ({
  groupedRoleAssignment,
  updateRoleAssignments,
  getRoles,
  roleAssignments,
  loggedInUserPermissions,
  loggedInUserProfile
}: RoleViewContainerProps) => {
  const loggedInUserPermissionsData = useLoggedInUserPermissions(loggedInUserPermissions)
  const [isEditing, setEditing] = useState(false)
  const handleUpdateRoleAssignments = async (groupedRoleAssignment: GroupedRoleAssignment) => {
    await updateRoleAssignments(groupedRoleAssignment)
    setEditing(false)
  }

  const {rolesByType} = useRolesConfigurationForUserId(loggedInUserProfile)

  const roleConfiguration: RoleConfiguration | undefined =
    rolesByType?.[groupedRoleAssignment.roleType]
  const isInternal =
    roleAssignments.find((item) => item.roleType === groupedRoleAssignment.roleType)?.isInternal ??
    false

  if (!loggedInUserPermissionsData) {
    return null
  }

  const canEdit =
    !!roleConfiguration && Object.keys(groupedRoleAssignment.unsupportedDataScopes).length === 0

  if (isEditing) {
    return (
      <RoleEditContainer
        groupedRoleAssignment={groupedRoleAssignment}
        updateRoleAssignments={handleUpdateRoleAssignments}
        onDiscard={() => setEditing(false)}
        loggedInUserProfile={loggedInUserProfile}
      />
    )
  }

  const commonDataScopes = Object.keys(groupedRoleAssignment.commonDataScopes).sort(sortDataScopes)
  const unsupportedDataScopes = Object.keys(groupedRoleAssignment.unsupportedDataScopes).sort(
    sortDataScopes
  )

  return (
    <RoleViewLayout
      commonDataScopes={commonDataScopes}
      unsupportedDataScopes={unsupportedDataScopes}
      isInternal={isInternal}
      canEdit={canEdit}
      groupedRoleAssignment={groupedRoleAssignment}
      getRoles={getRoles}
      setEditing={setEditing}
      roleAssignments={roleAssignments}
      loggedInUserPermissions={loggedInUserPermissionsData}
      loggedInUserProfile={loggedInUserProfile}
    />
  )
}
